import { Form, FormLayout, Modal, TextField } from '@shopify/polaris';
import { notEmpty, useField, useForm } from '@shopify/react-form';
import { useEffect } from 'react';
import helpers from 'helpers';
import { useMutateCreateAgribankCallback } from 'queries/transactions.query';

type TransactionsCallbackCreateModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function TransactionsCallbackCreateModal({ open, onClose }: TransactionsCallbackCreateModalProps) {
  const { mutateAsync: createCallback, isPending, isSuccess } = useMutateCreateAgribankCallback();

  const getCurrentLocalDateTime = () => {
    const now = new Date();
    const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
    return localTime.toISOString().slice(0, 16);
  };

  const required = notEmpty('Không được để trống trường này.');
  const positiveNumber = (value: string) => {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return 'Giá trị phải là một số lớn hơn 0.';
  };
  const validDateTime = (value: string) => {
    if (Number.isNaN(new Date(value).getTime())) return 'Thời gian giao dịch không hợp lệ.';
  };

  const formFields = {
    amount: useField<string>({ value: '', validates: [required, positiveNumber] }),
    content: useField<string>({ value: '', validates: [required] }),
    timestamp: useField<string>({ value: getCurrentLocalDateTime(), validates: [required, validDateTime] }),
    from: useField<string>({ value: '', validates: [required] }),
    from_name: useField<string>({ value: '', validates: [required] }),
  };

  const { fields, submit, dirty, reset } = useForm({
    fields: formFields,
    async onSubmit(values) {
      await createCallback({
        amount: Number(values.amount),
        content: values.content.trim(),
        timestamp: Math.floor(new Date(values.timestamp).getTime() / 1000),
        from: values.from.trim(),
        from_name: values.from_name.trim(),
      });

      return { status: 'success' as const };
    },
  });

  useEffect(() => {
    if (isSuccess) {
      reset();
      onClose();
    }
  }, [isSuccess]);

  const closeModal = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      title="Thêm giao dịch callback Agribank"
      primaryAction={{
        content: 'Gửi callback',
        disabled: !dirty,
        loading: isPending,
        onAction: submit,
      }}
      secondaryActions={[{ content: 'Đóng', onAction: closeModal }]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <TextField
              label="Số tiền (VND)"
              type="tel"
              inputMode="numeric"
              pattern="[0-9,]*"
              autoComplete="off"
              requiredIndicator
              {...fields.amount}
              value={helpers.formatNumberWithCommas(fields.amount.value)}
              onChange={(value) => fields.amount.onChange(value ? String(helpers.parseNumeric(value)) : '')}
            />
            <TextField label="Nội dung chuyển khoản" autoComplete="off" requiredIndicator {...fields.content} />
            <TextField
              label="Thời gian giao dịch"
              type="datetime-local"
              autoComplete="off"
              requiredIndicator
              helpText="Thời gian sẽ tự động chuyển sang Unix timestamp khi gửi."
              {...fields.timestamp}
            />
            <TextField label="Số tài khoản người gửi" autoComplete="off" requiredIndicator {...fields.from} />
            <TextField label="Tên người gửi" autoComplete="off" requiredIndicator {...fields.from_name} />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );
}
